// Content written from the brand record for an action (#132): the draft
// panel (body with its claims highlighted, the claim check, the signals it
// cites, its versions) and "Write from this action". Every verdict comes from
// the server (app/services/content_studio/claims.py); this file lays it out.

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { toast } from 'sonner';
import { PenLine, Sparkles } from 'lucide-react';
import {
  ASSET_TYPES, editDraft, getDraft, getDraftVersion, listContent, recheckDraft, resolveClaim, sendDraft,
  writeFromAction,
} from '@/api/content';
import type { AssetType, Claim, ClaimStatus, Draft, DraftSummary, DraftVersion, Signal } from '@/api/content';
import { PublishPanel } from './content-publish-panel';
import '../deep-signal/sections/action-card.css';

const MUTED = 'var(--muted-foreground)';
const TEAL = 'var(--alpha-teal)';

export const btn: CSSProperties = {
  fontSize: 'var(--text-xs)', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)',
  backgroundColor: 'var(--background)', color: 'var(--foreground)', cursor: 'pointer',
};

export const STATUS_STYLE: Record<ClaimStatus, { bg: string; fg: string; label: string }> = {
  supported: { bg: 'var(--success-bg)', fg: 'var(--success-text)', label: 'Supported' },
  unverifiable: { bg: 'var(--warning-bg)', fg: 'var(--warning-text)', label: 'Unverifiable' },
  contradicted: { bg: 'var(--danger-bg, var(--warning-bg))', fg: 'var(--infrared-coral)', label: 'Contradicted' },
};

const UNCHECKED_STYLE = { bg: 'var(--warning-bg)', fg: 'var(--warning-text)', label: 'Not yet checked' };

/** Supported by the rules, but no model pass has read the sentence: it still
 *  blocks publish until the claims are checked again. */
export function unchecked(c: Claim) {
  return c.status === 'supported' && !c.model_checked && !c.resolution;
}

function claimStyle(c: Claim) {
  return unchecked(c) ? UNCHECKED_STYLE : STATUS_STYLE[c.status];
}

function Pill({ claim }: { claim: Claim }) {
  const s = claimStyle(claim);
  return (
    <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, padding: '1px 8px', borderRadius: '999px',
                   backgroundColor: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{s.label}</span>
  );
}

function blocking(c: Claim) {
  return (c.status !== 'supported' && !c.resolution) || unchecked(c);
}

/** The body, with each claim's sentence marked where it can be found. */
export function HighlightedBody({ text, claims }: { text: string; claims: Claim[] }) {
  const spans: { start: number; end: number; claim: Claim }[] = [];
  for (const c of claims) {
    const at = text.indexOf(c.sentence);
    if (at >= 0 && !spans.some(s => at < s.end && at + c.sentence.length > s.start)) {
      spans.push({ start: at, end: at + c.sentence.length, claim: c });
    }
  }
  spans.sort((a, b) => a.start - b.start);
  const out: ReactNode[] = [];
  let i = 0;
  spans.forEach((s, n) => {
    if (s.start > i) out.push(text.slice(i, s.start));
    const style = claimStyle(s.claim);
    out.push(
      <mark key={n} title={`${style.label}${s.claim.note ? `: ${s.claim.note}` : ''}`} style={{
        backgroundColor: blocking(s.claim) ? style.bg : 'transparent', color: 'inherit',
        borderBottom: `2px solid ${style.fg}`, padding: '0 1px',
      }}>{text.slice(s.start, s.end)}</mark>,
    );
    i = s.end;
  });
  if (i < text.length) out.push(text.slice(i));
  return (
    <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--text-sm)', lineHeight: 1.6, color: 'var(--foreground)',
                  padding: '12px 14px', border: '1px solid var(--border)', borderRadius: '8px',
                  backgroundColor: 'var(--muted)', maxHeight: '480px', overflowY: 'auto' }}>
      {out}
    </div>
  );
}

/** Every claim, blocking ones first, with its source entry or why it blocks. */
export function ClaimList({ claims, onResolve, busy, canConfirm = false }: {
  claims: Claim[];
  onResolve?: (c: Claim, resolution: 'confirm_fact' | 'remove') => void;
  busy?: boolean;
  /** Confirming a claim as a fact writes it into the brand record: admins only. */
  canConfirm?: boolean;
}) {
  const sorted = [...claims].sort((a, b) => Number(blocking(b)) - Number(blocking(a)) || a.sentence_index - b.sentence_index);
  if (claims.length === 0) {
    return <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>No sentence states a number, date, name, place, price, certification or comparison.</p>;
  }
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {sorted.map(c => (
        <li key={c.sentence_index} style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 10px',
                                            fontSize: 'var(--text-xs)' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <Pill claim={c} />
            <span style={{ flex: 1, color: 'var(--foreground)' }}>{c.sentence}</span>
          </div>
          <div style={{ marginTop: '4px', color: MUTED }}>
            {c.kinds.join(', ')}
            {c.support.length > 0 && <> · {c.status === 'contradicted' ? 'contradicted by' : 'source'}{' '}
              {c.support.map((s, i) => (
                <span key={i} title={s.text}>{i > 0 && '; '}[{s.ref}] {s.label}
                  {s.record?.source_label ? ` (${s.record.source_label})` : ''}</span>
              ))}</>}
            {c.note && <> · {c.note}</>}
            {unchecked(c) && <> · not yet read by the model: check claims again</>}
            {c.resolution && <> · resolved: {c.resolution.action.replace('_', ' ')} by {c.resolution.by}</>}
          </div>
          {onResolve && blocking(c) && (
            <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
              {canConfirm && c.status === 'unverifiable' && (
                <button className="ds-act-btn" disabled={busy} onClick={() => onResolve(c, 'confirm_fact')}
                  title="It is true: record it in the brand record as a fact confirmed by you">Confirm as a fact</button>
              )}
              <button className="ds-act-btn ds-act-btn--danger" disabled={busy} onClick={() => onResolve(c, 'remove')}>Remove sentence</button>
              <span style={{ color: MUTED, alignSelf: 'center' }}>or edit the draft</span>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

export function SignalsCited({ signals }: { signals: Signal[] }) {
  if (!signals.length) return <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>No record entries cited.</p>;
  return (
    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-xs)', color: 'var(--foreground)' }}>
      {signals.map(s => (
        <li key={s.ref} style={{ marginBottom: '3px' }}>
          <b>[{s.ref}] {s.kind_label}</b>, {s.label}: <span style={{ color: MUTED }}>{s.text}</span>
          {s.record?.source_label && <span style={{ color: MUTED }}> · {s.record.source_label}</span>}
        </li>
      ))}
    </ul>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: '12px' }}>
      <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: MUTED, marginBottom: '6px' }}>{title}</p>
      {children}
    </div>
  );
}

/** One draft: latest version (or an older one, read-only), its claim check,
 *  the signals it cites, edit / resolve / re-check, send to publish. */
export function DraftPanel({ customerId, draftId, isAdmin, onClose }: {
  customerId: number; draftId: number; isAdmin: boolean; onClose?: () => void;
}) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [viewing, setViewing] = useState<DraftVersion | null>(null);
  const [editing, setEditing] = useState<{ title: string; body: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDraft(customerId, draftId).then(setDraft).catch(e => toast.error(e?.message || 'Could not load the draft'));
  }, [customerId, draftId]);

  if (!draft) return <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>Loading draft…</p>;
  const v = viewing ?? draft.latest;
  const latest = viewing == null;
  const sent = !!draft.sent_at;

  const act = async (fn: () => Promise<Draft>, done: string) => {
    setBusy(true);
    try {
      setDraft(await fn());
      setViewing(null);
      setEditing(null);
      toast.success(done);
    } catch (e: any) {
      toast.error(e?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };
  const n = draft.latest.version_number;
  const summary = v.claim_summary;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px', marginTop: '8px',
                  backgroundColor: 'var(--background)' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 'var(--text-sm)' }}>{draft.asset_label}</b>
        <span style={{ fontSize: 'var(--text-xs)', color: MUTED }}>
          v{v.version_number} by {v.author_kind === 'model' ? `the model (${v.author})` : v.author} · {v.change_kind.replace('_', ' ')}
          {!latest && ' · an older version, read only'}
        </span>
        {sent && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--success-text)' }}>Sent to publish (content item #{draft.action_item_id})</span>}
        {onClose && <button className="ds-act-btn" style={{ marginLeft: 'auto' }} onClick={onClose}>Close</button>}
      </div>
      <p style={{ fontSize: 'var(--text-xs)', marginTop: '4px',
                  color: draft.brief.evidence ? MUTED : 'var(--warning-text)' }}>
        {draft.brief.mode === 'free_text' ? 'No action, no evidence. ' : ''}{draft.brief.mode_note}
        {draft.brief.run_id ? ` Read: run ${draft.brief.run_id}.` : ''}
        {draft.brief.truth_set_version_id ? ` Truth set version ${draft.brief.truth_set_version_id}.` : ''}
      </p>
      {v.belief_explicit === false && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--warning-text)' }}>The model did not quote a sentence that states the belief explicitly.</p>
      )}

      {editing ? (
        <div style={{ marginTop: '10px' }}>
          <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })}
            style={{ width: '100%', fontSize: 'var(--text-sm)', padding: '6px 8px', borderRadius: '6px',
                     border: '1px solid var(--border)', marginBottom: '6px', boxSizing: 'border-box' }} />
          <textarea value={editing.body} onChange={e => setEditing({ ...editing, body: e.target.value })} rows={18}
            style={{ width: '100%', fontSize: 'var(--text-sm)', padding: '8px 10px', borderRadius: '6px',
                     border: '1px solid var(--border)', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
            <button className="ds-act-btn ds-act-btn--primary" disabled={busy} onClick={() => act(
              () => editDraft(customerId, draft.id, { base_version: n, title: editing.title, body: editing.body }),
              `Saved as version ${n + 1}`)}>Save as a new version</button>
            <button className="ds-act-btn" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: '10px' }}>
          {v.title && <p style={{ fontSize: 'var(--text-base)', fontWeight: 600, marginBottom: '6px' }}>{v.title}</p>}
          <HighlightedBody text={v.body} claims={v.claims} />
        </div>
      )}

      <Section title={`Claim check: ${summary.supported} supported, ${summary.unverifiable} unverifiable, ${summary.contradicted} contradicted`
                      + (summary.unchecked ? `, ${summary.unchecked} not yet checked` : '')
                      + (summary.model_check && summary.model_check !== 'ran' ? ` (model check ${summary.model_check})` : '')}>
        <ClaimList claims={v.claims} busy={busy} canConfirm={isAdmin}
          onResolve={latest && !sent ? (c, resolution) => act(
            () => resolveClaim(customerId, draft.id, c.sentence_index, { base_version: n, resolution }),
            resolution === 'remove' ? 'Sentence removed' : 'Recorded as a fact') : undefined} />
      </Section>

      <Section title="Signals cited (the record entries it drew on)">
        <SignalsCited signals={v.signals} />
      </Section>

      {v.policy_warnings.length > 0 && (
        <Section title="Content policy">
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-xs)' }}>
            {v.policy_warnings.map((w, i) => (
              <li key={i} style={{ color: w.severity === 'hard' ? 'var(--infrared-coral)' : MUTED }}>{w.message}</li>
            ))}
          </ul>
        </Section>
      )}

      {latest && !sent && !editing && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px', paddingTop: '10px',
                      borderTop: '1px dashed var(--border)' }}>
          <button className="ds-act-btn" onClick={() => setEditing({ title: v.title, body: v.body })}><PenLine size={13} /> Edit</button>
          <button className="ds-act-btn" disabled={busy} onClick={() => act(() => recheckDraft(customerId, draft.id, n), 'Claims checked again')}>
            Check claims again
          </button>
          {isAdmin && (
            <button className={v.publish_blockers.length ? 'ds-act-btn' : 'ds-act-btn ds-act-btn--primary'} disabled={busy || v.publish_blockers.length > 0}
              title={v.publish_blockers.join('. ') || 'Becomes a content item on the action; the wind tunnel and publish gate take over'}
              onClick={() => act(() => sendDraft(customerId, draft.id, n), 'Sent to publish')}>
              Send to publish
            </button>
          )}
          {v.publish_blockers.length > 0 && (
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--warning-text)', alignSelf: 'center' }}>
              {v.publish_blockers.join('. ')}
            </span>
          )}
        </div>
      )}

      {latest && !editing && <PublishPanel customerId={customerId} draft={draft} isAdmin={isAdmin} />}

      <Section title="Versions">
        <ol style={{ margin: 0, paddingLeft: '18px', fontSize: 'var(--text-xs)', color: MUTED }}>
          {draft.versions.map(x => (
            <li key={x.version_number}>
              <button style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit',
                               color: x.version_number === v.version_number ? 'var(--foreground)' : TEAL }}
                onClick={async () => {
                  if (x.version_number === n) { setViewing(null); return; }
                  try { setViewing(await getDraftVersion(customerId, draft.id, x.version_number)); }
                  catch (e: any) { toast.error(e?.message || 'Could not load that version'); }
                }}>v{x.version_number}</button>{' '}
              {x.author_kind === 'model' ? 'model' : x.author} · {x.change_kind.replace('_', ' ')}
              {x.change_note ? ` · ${x.change_note}` : ''} · {new Date(x.created_at).toLocaleString()}
              {x.claim_summary?.blocking ? ` · ${x.claim_summary.blocking} blocking` : ''}
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}

export function draftLine(d: DraftSummary): string {
  const s = d.claim_summary || {};
  const state = d.sent_at ? 'sent to publish' : s.blocking ? `${s.blocking} claim${s.blocking === 1 ? '' : 's'} to resolve` : 'claims supported';
  return `${d.asset_label}${d.title ? `: ${d.title}` : ''} (v${d.version_number}, ${state})`;
}

/** "Write from this action": pick asset types, write, open the drafts. */
export function WriteFromAction({ customerId, actionId, isAdmin, blueprintItemId }: {
  customerId: number; actionId: number; isAdmin: boolean; blueprintItemId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState<Set<AssetType>>(new Set(['page']));
  const [instructions, setInstructions] = useState('');
  const [drafts, setDrafts] = useState<DraftSummary[] | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [openDraft, setOpenDraft] = useState<number | null>(null);
  const [writing, setWriting] = useState(false);

  const load = () => listContent(customerId, actionId)
    .then(r => { if (!r.available) setUnavailable(r.migration || 'the content migration'); setDrafts(r.drafts); })
    .catch(() => setDrafts([]));
  useEffect(() => { if (open && drafts == null) load(); }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps

  const write = async () => {
    setWriting(true);
    try {
      const r = await writeFromAction(customerId, actionId, {
        asset_types: [...types], instructions: instructions.trim() || undefined, blueprint_item_id: blueprintItemId ?? null,
      });
      toast.success(`${r.drafts.length} draft${r.drafts.length === 1 ? '' : 's'} written from the brief`);
      await load();
      if (r.drafts[0]) setOpenDraft(r.drafts[0].id);
    } catch (e: any) {
      toast.error(e?.message || 'Could not write from this action');
    } finally {
      setWriting(false);
    }
  };

  return (
    <div>
      <button className="ds-act-btn" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <PenLine size={13} /> {open ? 'Hide writing' : 'Write from this action'}
      </button>
      {open && (
        <div style={{ marginTop: '10px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: '8px',
                      backgroundColor: 'var(--background)' }}>
          {unavailable ? (
            <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>Not available yet: apply {unavailable} first.</p>
          ) : (
            <>
              <p style={{ fontSize: 'var(--text-xs)', color: MUTED, lineHeight: 1.5, marginBottom: '10px' }}>
                The brief is assembled from the brand record for this action: the belief's figures, the judge's quotes,
                win reasons, confirmed facts, market findings, the voice profile and precedent. Every claim in a draft is
                checked against it.
              </p>
              <div className="ds-act-section__label" style={{ marginBottom: '6px' }}>What to write</div>
              <div className="ds-act-toolbar" role="group" aria-label="Asset types">
                {ASSET_TYPES.map(t => (
                  <button key={t.id} type="button" className="ds-act-btn" aria-pressed={types.has(t.id)} onClick={() => {
                    const next = new Set(types);
                    if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                    setTypes(next);
                  }}>{t.label}</button>
                ))}
              </div>
              <input value={instructions} onChange={e => setInstructions(e.target.value)}
                placeholder="Anything else the writer should know (optional)"
                style={{ width: '100%', marginTop: '10px', fontSize: 'var(--text-xs)', height: '30px', padding: '0 10px',
                         borderRadius: '6px', border: '1px solid var(--border)', boxSizing: 'border-box',
                         backgroundColor: 'var(--background)', color: 'var(--foreground)' }} />
              <button className="ds-act-btn ds-act-btn--primary" style={{ marginTop: '10px' }} disabled={writing || types.size === 0} onClick={write}>
                <Sparkles size={13} /> {writing ? 'Writing…' : `Write ${types.size} draft${types.size === 1 ? '' : 's'}`}
              </button>
            </>
          )}
          {drafts && drafts.length > 0 && (
            <ul style={{ margin: '10px 0 0', paddingLeft: '18px', fontSize: 'var(--text-xs)' }}>
              {drafts.map(d => (
                <li key={d.id}>
                  <button style={{ border: 'none', background: 'none', padding: 0, color: TEAL, cursor: 'pointer', fontSize: 'inherit' }}
                    onClick={() => setOpenDraft(openDraft === d.id ? null : d.id)}>{draftLine(d)}</button>
                </li>
              ))}
            </ul>
          )}
          {openDraft != null && (
            <DraftPanel customerId={customerId} draftId={openDraft} isAdmin={isAdmin}
              onClose={() => { setOpenDraft(null); load(); }} />
          )}
        </div>
      )}
    </div>
  );
}
